import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import { loginUser } from '../utils/api';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate   = useNavigate();

  const [form,     setForm]     = useState({ email: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [errors,   setErrors]   = useState({});

  // Forgot password state
  // step: 'login' | 'forgot' | 'otp' | 'reset'
  const [step,        setStep]        = useState('login');
  const [fpEmail,     setFpEmail]     = useState('');
  const [fpOtp,       setFpOtp]       = useState('');
  const [fpPassword,  setFpPassword]  = useState('');
  const [fpPassword2, setFpPassword2] = useState('');
  const [fpLoading,   setFpLoading]   = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = {};
    if (!form.email)    errs.email    = 'Email required';
    if (!form.password) errs.password = 'Password required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await loginUser(form);
      login(res.data.token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.fullName.split(' ')[0]}!`);
      if (res.data.user.role === 'admin')        navigate('/admin');
      else if (res.data.user.role === 'teacher') navigate('/teacher');
      else                                        navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  // Step 1 — send OTP to email
  const handleForgotSendOtp = async () => {
    if (!fpEmail || !/\S+@\S+\.\S+/.test(fpEmail)) {
      toast.error('Enter a valid email address.');
      return;
    }
    setFpLoading(true);
    try {
      await API.post('/auth/forgot-password', { email: fpEmail });
      toast.success('OTP sent to your email!');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setFpLoading(false);
    }
  };

  // Step 2 — verify OTP and reset password
  const handleResetPassword = async () => {
    if (!fpOtp || fpOtp.length !== 6) { toast.error('Enter the 6-digit OTP.'); return; }
    if (!fpPassword || fpPassword.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    if (fpPassword !== fpPassword2) { toast.error('Passwords do not match.'); return; }

    setFpLoading(true);
    try {
      await API.post('/auth/reset-password', {
        email:       fpEmail,
        otp:         fpOtp,
        newPassword: fpPassword,
      });
      toast.success('Password reset successfully! Please login with your new password.');
      // Reset all forgot password state and go back to login
      setStep('login');
      setFpEmail(''); setFpOtp(''); setFpPassword(''); setFpPassword2('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setFpLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '80px 16px 32px' }}>
      <style>{`
        .auth-card { background: white; border-radius: var(--radius-xl); box-shadow: var(--shadow-lg); padding: 44px; width: 100%; max-width: 440px; border: 1px solid var(--border); }
        .auth-logo { text-align: center; margin-bottom: 32px; }
        .auth-logo-icon { width: 52px; height: 52px; background: linear-gradient(135deg, var(--primary), var(--accent)); border-radius: 14px; display: flex; align-items: center; justify-content: center; color: white; font-size: 26px; font-weight: 800; margin: 0 auto 12px; font-family: var(--font-display); }
        .auth-logo h2 { font-size: 22px; margin-bottom: 4px; }
        .auth-logo p { color: var(--mid); font-size: 14px; }
        .auth-input-wrap { position: relative; }
        .auth-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: var(--mid); pointer-events: none; }
        .auth-input { padding-left: 42px !important; }
        .auth-input-toggle { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); color: var(--mid); cursor: pointer; background: none; border: none; display: flex; }
        .auth-divider { display: flex; align-items: center; gap: 12px; margin: 20px 0; }
        .auth-divider::before, .auth-divider::after { content: ''; flex: 1; height: 1px; background: var(--border); }
        .auth-divider span { font-size: 13px; color: var(--mid); }
        .otp-input { letter-spacing: 8px; font-size: 22px; font-weight: 700; text-align: center; font-family: monospace; }
        .back-btn { background: none; border: none; color: var(--primary); cursor: pointer; font-size: 13px; display: flex; align-items: center; gap: 5px; padding: 0; margin-bottom: 20px; font-weight: 600; }
        .back-btn:hover { text-decoration: underline; }
        .fp-step-indicator { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 24px; }
        .fp-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--border); }
        .fp-dot.active { background: var(--primary); width: 24px; border-radius: 4px; }
      `}</style>

      <div className="auth-card">

        {/* ── LOGIN FORM ── */}
        {step === 'login' && (
          <>
            <div className="auth-logo">
              <div className="auth-logo-icon">K</div>
              <h2>Welcome Back</h2>
              <p>Login to your Korpnex account</p>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label>Email Address</label>
                <div className="auth-input-wrap">
                  <FiMail size={16} className="auth-input-icon" />
                  <input name="email" type="email" value={form.email} onChange={handleChange}
                    className={`form-control auth-input${errors.email ? ' error' : ''}`}
                    placeholder="you@example.com" />
                </div>
                {errors.email && <div className="form-error">{errors.email}</div>}
              </div>

              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <label style={{ margin: 0 }}>Password</label>
                  <button type="button" onClick={() => { setStep('forgot'); setFpEmail(form.email); }}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 13, cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                    Forgot password?
                  </button>
                </div>
                <div className="auth-input-wrap">
                  <FiLock size={16} className="auth-input-icon" />
                  <input name="password" type={showPass ? 'text' : 'password'} value={form.password}
                    onChange={handleChange}
                    className={`form-control auth-input${errors.password ? ' error' : ''}`}
                    placeholder="Your password" />
                  <button type="button" className="auth-input-toggle" onClick={() => setShowPass(s => !s)}>
                    {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
                {errors.password && <div className="form-error">{errors.password}</div>}
              </div>

              <button type="submit" className="btn btn-primary btn-lg"
                style={{ width: '100%', justifyContent: 'center' }} disabled={loading}>
                {loading ? <><div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Logging in...</> : 'Login to Dashboard'}
              </button>
            </form>

            <div className="auth-divider"><span>or</span></div>
            <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--mid)' }}>
              New to Korpnex? <Link to="/register" style={{ color: 'var(--primary)', fontWeight: 600 }}>Create Account</Link>
            </p>
            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--mid)', marginTop: 8 }}>
              Want to apply first? <Link to="/apply" style={{ color: 'var(--accent)', fontWeight: 600 }}>Apply Now</Link>
            </p>
          </>
        )}

        {/* ── FORGOT PASSWORD — Enter Email ── */}
        {step === 'forgot' && (
          <>
            <button className="back-btn" onClick={() => setStep('login')}>
              <FiArrowLeft size={14} /> Back to Login
            </button>
            <div className="auth-logo">
              <div className="auth-logo-icon">🔑</div>
              <h2>Forgot Password?</h2>
              <p>Enter your registered email to receive an OTP</p>
            </div>
            <div className="fp-step-indicator">
              <div className="fp-dot active" />
              <div className="fp-dot" />
            </div>
            <div className="form-group">
              <label>Registered Email</label>
              <div className="auth-input-wrap">
                <FiMail size={16} className="auth-input-icon" />
                <input type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)}
                  className="form-control auth-input" placeholder="you@example.com" autoFocus />
              </div>
            </div>
            <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleForgotSendOtp} disabled={fpLoading}>
              {fpLoading ? <><div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Sending OTP...</> : 'Send OTP to Email'}
            </button>
          </>
        )}

        {/* ── FORGOT PASSWORD — Enter OTP + New Password ── */}
        {step === 'otp' && (
          <>
            <button className="back-btn" onClick={() => setStep('forgot')}>
              <FiArrowLeft size={14} /> Change Email
            </button>
            <div className="auth-logo">
              <div className="auth-logo-icon">✉️</div>
              <h2>Enter OTP</h2>
              <p>OTP sent to <strong>{fpEmail}</strong></p>
            </div>
            <div className="fp-step-indicator">
              <div className="fp-dot" />
              <div className="fp-dot active" />
            </div>

            <div className="form-group">
              <label>6-digit OTP</label>
              <input type="text" value={fpOtp} onChange={e => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="form-control otp-input" placeholder="000000" maxLength={6} autoFocus />
            </div>

            <div className="form-group">
              <label>New Password</label>
              <div className="auth-input-wrap">
                <FiLock size={16} className="auth-input-icon" />
                <input type={showNewPass ? 'text' : 'password'} value={fpPassword}
                  onChange={e => setFpPassword(e.target.value)}
                  className="form-control auth-input" placeholder="Min 6 characters" />
                <button type="button" className="auth-input-toggle" onClick={() => setShowNewPass(s => !s)}>
                  {showNewPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Confirm New Password</label>
              <div className="auth-input-wrap">
                <FiLock size={16} className="auth-input-icon" />
                <input type={showNewPass ? 'text' : 'password'} value={fpPassword2}
                  onChange={e => setFpPassword2(e.target.value)}
                  className="form-control auth-input" placeholder="Repeat password" />
              </div>
              {fpPassword && fpPassword2 && fpPassword !== fpPassword2 && (
                <div className="form-error">Passwords do not match</div>
              )}
            </div>

            <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
              onClick={handleResetPassword} disabled={fpLoading}>
              {fpLoading ? <><div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> Resetting...</> : 'Reset Password'}
            </button>

            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--mid)', marginTop: 16 }}>
              Didn't receive OTP?{' '}
              <button type="button" onClick={() => setStep('forgot')}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}>
                Resend OTP
              </button>
            </p>
          </>
        )}

      </div>
    </div>
  );
}